/**
 * Per-user rewarded ad-free mechanism — shared contract.
 *
 * The whole mechanism (CTA, slot teardown/rebuild, 1h timer, gating) runs
 * against a provider that is hidden behind this interface + DI token. The
 * dummy provider (local dev) and the real AppLixirRewardedAdProvider are
 * swapped via a single provider line in app.module, nothing else changes.
 *
 * There is deliberately NO dialog and NO injection element in this contract:
 * the AppLixir v6 player always presents itself as its own fixed fullscreen
 * modal (`.applixir-backdrop`, verified in the SDK source) — the injection div
 * is just a DOM anchor whose size/position is irrelevant. Each provider
 * therefore creates and cleans up its own anchor element per call.
 *
 * See monetization/applixir-rewarded-handover-2026-06-23.md §2 / §13.
 */

export type RewardedAdStatus =
    | 'complete'
    | 'skipped'
    | 'closed'
    | 'error'
    | 'adblocked'
    | 'no-ad';

export interface RewardedAdResult {
    status: RewardedAdStatus;
}

export interface RewardedAdOptions {
    /**
     * Called when the SDK reports a completed watch AFTER the flow already
     * settled via watchdog timeout (e.g. the user sat on the consent dialog
     * past the timeout, then accepted and watched the full video). The promise
     * has long resolved at that point — this hook lets the caller still grant
     * the reward. Fires at most once, and never after a regular settle.
     */
    onLateComplete?: () => void;
}

/**
 * Injection token (abstract class doubles as the Angular DI token).
 *
 * The reward (ad-free) is granted exclusively when the result status is
 * 'complete'. Every other status grants nothing.
 */
export abstract class RewardedAdProvider {
    /**
     * Opens the rewarded player (the provider presents its own overlay UI) and
     * resolves as soon as the ad flow ends — regardless of complete/skip/error.
     * Never rejects; failures resolve with status 'error'.
     */
    abstract showRewardedAd(options?: RewardedAdOptions): Promise<RewardedAdResult>;
}
